import { AuthContextProvider } from '@/hooks/useAuthContext';
import '@/i18n';
import { AuthenticatedRootComponent } from '@/components/organisms/AuthenticatedRootComponent';
import { oktaAuth as rootOktaAuth } from '@/utils/okta';
import { Toaster } from '@liza/ui/components/ui/sonner';
import { TooltipProvider } from '@liza/ui/components/ui/tooltip';
import { useSentryFeedbackHotkey } from '@liza/ui/hooks/useSentryFeedbackHotkey';
import {
  getSentryFeedbackNameEmailTextDefaults,
  getSentryFeedbackTextDefaults,
} from '@liza/ui/utils/sentryFeedbackTextDefaults';
import type { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { createRootRouteWithContext, HeadContent, Outlet, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
export type RouterAppContext = {};

const RootComponent = (): React.ReactNode => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useSentryFeedbackHotkey({
    ...getSentryFeedbackTextDefaults(t),
    ...getSentryFeedbackNameEmailTextDefaults(t),
    formTitle: t('reportIssue'),
    messagePlaceholder: t('reportIssuePlaceholder'),
    submitButtonLabel: t('sendFeedback'),
    showBranding: false,
  });

  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string): void => {
    void navigate({ to: originalUri || '/' });
  };

  return (
    <>
      <HeadContent />
      <Security oktaAuth={rootOktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <AuthContextProvider>
          <AuthenticatedRootComponent>
            <TooltipProvider>
              <Toaster />
              <Outlet />
            </TooltipProvider>
          </AuthenticatedRootComponent>
        </AuthContextProvider>
      </Security>
    </>
  );
};

export const Route = createRootRouteWithContext<RouterAppContext>()({
  component: RootComponent,
  head: () => ({
    links: [{ href: '/favicon.png', type: 'image/png', rel: 'icon' }],
    meta: [{ charSet: 'utf-8' }, { content: 'width=device-width, initial-scale=1.0', name: 'viewport' }],
  }),
});
